import { css } from '@emotion/react';
import { onMobile } from '@styles/responsive';
import {
  Button,
  color,
  DropZone,
  DropZoneV2,
  fileRejectionsType,
  FormGroup,
  Icon,
  IDropZoneProps,
  Input,
  Modal,
  RatioStyle,
  shadow,
  SizeStyle,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';

import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { reorder, makeFormData, checkFileSize, ModalContent, toast } from '@/utils';
import { unsplash } from '@/unsplash';
import { ColumnFlex, PositionRelative, TextAlignCenter, Width100 } from '@/styles';
import {
  useDebounce,
  useInput,
  useIntersectionObserver,
  useDidUpdateEffect,
  useToggle,
  useModalState,
  useIsMobile,
  useRequest,
  usePostRequest,
  useBrowserDownload,
  useFileUpload,
  useParamsWithMatchPath,
  useAiCreditCheck
} from '@/hooks';
import { coreApi } from '@/api';
import AiCreateButton from '@/pages/ai/components/AiCreateButton';
import CreateAiImageModalTemplate from '@/pages/ai/components/CreateAiImageModalTemplate';
import AiLoadingBox from '@/pages/ai/components/AiLoadingBox';
import AiUsageNotice from '@/components/AiUsageNotice';
import { ClickEventName, PageViewEventName, track } from '@/track';

export interface UploaderProps extends Pick<IDropZoneProps, 'ratio' | 'size' | 'accepts'> {
  /**
   * unsplash 검색 인풋 렌더링 여부입니다.
   * @default false
   */
  hasSearch?: boolean;
  /**
   * 세로 레이아웃 여부입니다.
   * @default false
   */
  isVerticalLayout?: boolean;
  /**
   * 다중 이미지 업로드 여부입니다.
   * @default false
   */
  isMultiple?: boolean;
  // UserFileResponseModel을 반환합니다.
  getUploadFiles: (files?: UserFileResponseModel[]) => void;
  /**
   * 업로드 로딩 상태를 반환합니다.
   */
  getUploadLoadingStatus?: (isUploaded: boolean) => void;
  /**
   * 이미 업로드 되어 있는 파일을 받아옵니다.
   */
  uploadedFiles?: UserFileResponseModel[];
  // 최대 업로드 가능한 이미지 갯수입니다.
  limit?: number;
  id?: string;
  /**
   * 업로드 용량 제한입니다.
   */
  maxFileSize?: number;
  useFor?: string;

  /**
   * AI 프로젝트에 사용되는 이미지 업로더 여부를 파악합니다.
   */
  isAiProject?: boolean;

  /**
   * AI 프로젝트의 이미지 단건 요청 시, Ratio 설정값입니다.
   */
  aiImageRatio?: '3:4' | '4:3' | '9:16' | '1:1';

  imageUseForType?: string;
}

interface UploadImagePreviewMarkupProps extends Required<Pick<IDropZoneProps, 'ratio' | 'size'>> {
  uploadFile: UserFileResponseModel;
  onDeletePhoto: (selectFileId: string) => void;
  isMultiple?: boolean;
}

const MULTIPLE = {
  WIDTH: {
    small: '100px',
    medium: '160px'
  },
  HEIGHT: {
    small: '100px',
    medium: '200px'
  }
};

const UploadImagePreviewMarkup = ({
  uploadFile,
  onDeletePhoto,
  isMultiple,
  ratio,
  size
}: UploadImagePreviewMarkupProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const MultipleImageSizeStyle = css`
    min-width: ${MULTIPLE.WIDTH[size]};
    max-width: 225px;
    height: ${MULTIPLE.HEIGHT[size]};
  `;

  const SizeStyle = (() => {
    if (isMultiple && size === 'small') return [MultipleImageSizeStyle, RatioStyle[ratio]];
    if (isMultiple) return MultipleImageSizeStyle;

    return RatioStyle[ratio];
  })();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleDialog = (event?: unknown) => {
    setIsDialogOpen((isDialogOpen) => !isDialogOpen);
  };

  const handlers = {
    onClick: toggleDialog,
    onKeyDown: toggleDialog
  };

  return (
    <>
      <img
        css={[BaseImageStyle, SizeStyle]}
        alt={uploadFile.id}
        src={uploadFile.url}
        {...handlers}
      />
      {isDialogOpen && (
        <div role="dialog" data-studio-overlay="true" css={imagePreviewDialog} {...handlers}>
          <div>
            <img
              alt={uploadFile.id}
              src={uploadFile.url}
              tabIndex={-1}
              onKeyDown={stopPropagation}
              onClick={stopPropagation}
            />
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div css={DeleteButtonStyle} onKeyDown={stopPropagation} onClick={stopPropagation}>
              <Button
                cssStyle={PosIconButtonStyle}
                icon="times_regular"
                type="plainWhite"
                size="medium"
                {...handlers}
              />
            </div>
          </div>
        </div>
      )}
      <div css={[SizeStyle, DeleteButtonStyle]}>
        <Button
          cssStyle={PosIconButtonStyle}
          onClick={() => onDeletePhoto(uploadFile.id)}
          icon="times_regular"
          type="plainWhite"
          size="medium"
        />
      </div>
    </>
  );
};

function stopPropagation(event: React.KeyboardEvent | React.MouseEvent) {
  event.stopPropagation();
}

const Uploader = ({
  hasSearch = false,
  isVerticalLayout = false,
  isMultiple = false,
  getUploadLoadingStatus,
  getUploadFiles,
  uploadedFiles,
  limit,
  ratio = 'fullWidth',
  size = 'medium',
  id,
  accepts,
  maxFileSize,
  useFor,
  isAiProject = false,
  aiImageRatio,
  imageUseForType
}: UploaderProps) => {
  const { appId } = useParams<AppParam>();
  const { data: aiProductData } =
    useRequest<CommonResponseModel<AiProductResponseModel[]>>(`/api/products/ai`);
  const { mutate: creditRefetch } = useRequest<CommonResponseModel<AiProductResponseModel[]>>(
    `/apps/${appId}/credit`
  );

  const [shouldMoreUnsplashFetch, setShouldMoreUnsplashFetch] = useState(false);

  const [uploadFiles, setUploadFiles] = useState(uploadedFiles);
  const [acceptedFilesLength, setAcceptedFilesLength] = useState(0);
  const [rejectedFiles, setRejectedFiles] = useState<fileRejectionsType>([]);
  const { modal, openModal, closeModal } = useModalState();
  const isMobile = useIsMobile();
  const { projectId } = useParamsWithMatchPath();
  const { checkCreditAndExecute, modal: aiCreditModal } = useAiCreditCheck();

  const { value: isLoading, setTrue: startLoading, setFalse: completeLoading } = useToggle(false);
  const { value: isUploaded, setTrue: completeUploaded, setFalse: failUploaded } = useToggle(false); // upload 완료 여부

  // useFileUpload hook for button file upload
  const buttonFileUpload = useFileUpload({
    type: 'image',
    multiple: isMultiple,
    useFor,
    maxFileSize,
    onSuccess: (files) => {
      setUploadFiles((prev) => (isMultiple ? [...(prev ?? []), ...files] : files));
      completeUploaded();
      completeLoading(); // 업로드 완료 시 isLoading을 false로 설정
    },
    onError: () => {
      failUploaded();
      completeLoading(); // 업로드 에러 시에도 isLoading을 false로 설정
    }
  });

  const [alertContent, setAlertContent] = useState<ModalContent>();
  const isAlertOpen = alertContent !== undefined;

  const [unsplashSearchQuery, setUnsplashSearchQuery] = useInput('');
  const unsplashPageIndex = useRef(1);
  const unsplashTotalPage = useRef(0);
  const {
    value: isImageLoading,
    setTrue: starImageLoading,
    setFalse: completeImageLoading
  } = useToggle(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const [unsplashSearchResult, setUnsplashSearchResult] = useState<UnsplashResultsEntity[]>([]);

  const debounceSearchPhotoTerm = useDebounce(unsplashSearchQuery, 500);

  function closeAlert() {
    setAlertContent(undefined);
  }

  function openImageErrorAlert() {
    setAlertContent({
      title: '업로드한 이미지를 확인해 주세요.',
      message: (
        <div css={openImageErrorAlertStyle}>
          이미지는 <strong>JPG</strong> 형식만 가능하며 <strong>최대 4MB까지</strong>만 업로드할 수
          있어요. <br />
          <br />
          <a href="https://imagecompressor.com/ko/" target="blank">
            이미지 최적화 도구
          </a>
          로 이미지 용량을 줄이면 더욱 빠르게 이미지를 불러올 수 있어요!
        </div>
      )
    });
  }

  const onChangeFile = useCallback(
    async (files: File[]) => {
      if (!isMultiple && files.length > 1) {
        setAlertContent({ title: '하나의 이미지만 올릴 수 있어요', message: '다시 시도해주세요' });
        return;
      }

      if (isMultiple && limit && (uploadFiles?.length ?? 0) + files.length > limit) {
        setAlertContent({
          title: `이미지는 ${limit}개까지 올릴 수 있어요`,
          message: '다시 시도해주세요'
        });
        return;
      }

      function fetchUploadImage(file: File) {
        startLoading();
        failUploaded();

        return coreApi.post<UserFileResponseModel>(
          `/apps/${appId}/images/uploadFile`,
          makeFormData({ file }),
          { params: { useFor } }
        );
      }

      // 한개의 파일만 업로드 할 때 허용하지 않은 형식인 경우 빈 배열입니다.
      if (files.length === 0) return;

      const filteredFiles = [...files].filter((file) => !checkFileSize({ file, maxFileSize }));

      if (filteredFiles.length === 0) {
        openImageErrorAlert();
      }

      setAcceptedFilesLength(files.length);

      try {
        const uploadedFiles = await (
          await Promise.all(filteredFiles.map(fetchUploadImage))
        ).map(({ data }) => data);
        setUploadFiles((uploadFiles) => [...(uploadFiles ?? []), ...uploadedFiles]);
        completeLoading();
        if (files.length > 0 && uploadedFiles.length > 0) completeUploaded();
      } catch (error) {
        completeLoading();
        failUploaded();
      }
    },
    [isMultiple, uploadFiles, isLoading, isUploaded]
  );

  useDidUpdateEffect(() => {
    if (uploadedFiles !== uploadFiles) {
      setUploadFiles(uploadedFiles);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    if (getUploadLoadingStatus === undefined) return;

    getUploadLoadingStatus(isLoading);
  }, [isLoading]);

  useDidUpdateEffect(() => {
    getUploadFiles(uploadFiles);
  }, [uploadFiles]);

  useEffect(() => {
    if (rejectedFiles.length) {
      failUploaded();
      openImageErrorAlert();
    }
  }, [rejectedFiles]);

  const onDeletePhoto = (selectFileId: string) => {
    const tempArr = uploadFiles?.filter((file) => file.id !== selectFileId);
    setUploadFiles(tempArr);
    failUploaded();
  };

  const SearchInputStyle = css`
    margin-left: ${isVerticalLayout ? 0 : spacing.common.medium};

    ${onMobile} {
      margin-left: 0px;
    }
  `;

  useEffect(() => {
    if (hasSearch && debounceSearchPhotoTerm) {
      searchImage(debounceSearchPhotoTerm);
    }
  }, [hasSearch && debounceSearchPhotoTerm]);

  const searchImage = useCallback(
    (query: string) => {
      if (!query) return;

      setUnsplashSearchQuery(query);
      unsplashPageIndex.current = 1;
      getUnsplashPhoto({ query, page: 1 }).then((res) => {
        setUnsplashSearchResult(res.results ?? []);
      });
    },
    [unsplashSearchQuery, unsplashPageIndex.current, unsplashSearchResult]
  );

  const getUnsplashPhoto = useCallback(
    ({ query, page }: { query: string; page: number }) => {
      starImageLoading();

      const params = {
        query,
        page
      };

      const data = unsplash.search(params).then((data: Unsplash) => {
        unsplashTotalPage.current = data.total_pages;
        completeImageLoading();

        return data;
      });

      return data;
    },
    [isImageLoading, unsplashTotalPage.current]
  );

  useIntersectionObserver({
    root: rootRef.current,
    target: targetRef.current,
    onIntersect: ([{ isIntersecting }]) => {
      if (
        shouldMoreUnsplashFetch &&
        isIntersecting &&
        !isImageLoading &&
        unsplashPageIndex.current < unsplashTotalPage.current
      ) {
        loadMoreImage();
      }
    },
    threshold: 0.0
  });

  const onChangeIsRenderPhotoList = useCallback(
    (node) => {
      if (node !== null) {
        setShouldMoreUnsplashFetch(true);
      } else {
        setShouldMoreUnsplashFetch(false);
      }
    },
    [shouldMoreUnsplashFetch]
  );

  const loadMoreImage = useCallback(() => {
    if (unsplashSearchResult.length > 0) {
      unsplashPageIndex.current += 1;

      getUnsplashPhoto({ query: unsplashSearchQuery, page: unsplashPageIndex.current }).then(
        (res) => {
          setUnsplashSearchResult((prevState) => [...prevState, ...(res.results || [])]);
        }
      );
    }
  }, [unsplashPageIndex.current, unsplashSearchResult]);

  const searchInputMarkup = (
    <FormGroup
      label="혹은 이미지 검색"
      helpText="Search photos by Unsplash"
      cssStyle={[GrowStyle, SearchInputStyle]}
    >
      <Input
        name="unsplashSearchQuery"
        placeholder="영문 키워드로 검색"
        onChange={setUnsplashSearchQuery}
        onKeyPress={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
        value={unsplashSearchQuery}
        prefix={<Icon icon="search_solid" size="20px" />}
      />
    </FormGroup>
  );

  const statePhotoListContainerStyle = css`
    margin-left: ${isVerticalLayout ? 0 : spacing.common.medium};

    ${onMobile} {
      margin-left: 0px;
    }
  `;

  async function onClickUploadUrl(uploadPhoto: UnsplashResultsEntity) {
    startLoading();
    failUploaded();

    try {
      const { url } = await unsplash.getUrl(uploadPhoto);

      const { data: uploadedFile } = await coreApi.post<UserFileResponseModel>(
        `/apps/${appId}/images/uploadUrl`,
        makeFormData({ url }),
        { params: { useFor } }
      );

      if (!isMultiple) {
        setUploadFiles([uploadedFile]);
      } else {
        setUploadFiles((uploadFiles) => [...(uploadFiles ?? []), uploadedFile]);
      }

      completeLoading();
      completeUploaded();
    } catch {
      completeLoading();
      failUploaded();
    }
  }

  const photoListMarkup = (
    <section css={[PhotoListContainerStyle, statePhotoListContainerStyle]} ref={rootRef}>
      {unsplashSearchResult.map((photo) => {
        const statePhotoImageStyle = css`
          ${`background-image: url(${photo.urls.thumb})`};
        `;

        return (
          <div
            key={photo.id}
            css={PhotoCardStyle}
            onClick={() => onClickUploadUrl(photo)}
            onKeyDown={() => onClickUploadUrl(photo)}
            role="button"
            tabIndex={0}
          >
            <div css={[FullContents, PhotoImgStyle, statePhotoImageStyle]} />
          </div>
        );
      })}
      <div ref={targetRef} />
      <div ref={onChangeIsRenderPhotoList} />
    </section>
  );

  const SingleContainerStateStyle = css`
    ${isVerticalLayout && !isMultiple && `flex-direction: column;`};
    ${!isVerticalLayout && !isMultiple && `gap: 16px; align-items: end;`};
  `;

  const MultipleDropZoneHeightStyle = css`
    height: ${MULTIPLE.HEIGHT[size]};
    margin-right: ${spacing.common.small};
    width: ${MULTIPLE.WIDTH[size]};
    flex-shrink: 0;

    .DragDrop-label {
      height: ${MULTIPLE.HEIGHT[size]};
      width: ${MULTIPLE.WIDTH[size]};
    }
  `;

  function onDragEnd({ destination, source }: DropResult) {
    if (!destination || !uploadFiles) return;

    const reordered = reorder(uploadFiles, source.index, destination.index);

    setUploadFiles(reordered);
  }

  const imagePreviewMarkups = uploadFiles?.map((uploadFile, index) =>
    isMultiple ? (
      <Draggable
        key={uploadFile.id ?? uploadFile.url}
        draggableId={uploadFile.id ?? uploadFile.url}
        index={index}
      >
        {(provided, snapshot) => (
          <li
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={snapshot.isDragging ? 'dragging' : 'no'}
            css={[
              PositionRelative,
              SizeStyle[size],
              imagePreviewWrapStyle,
              size === 'small' && SmallSizeLayout
            ]}
          >
            <UploadImagePreviewMarkup
              uploadFile={uploadFile}
              onDeletePhoto={onDeletePhoto}
              isMultiple={isMultiple}
              ratio={ratio}
              size={size}
            />
          </li>
        )}
      </Draggable>
    ) : (
      <div
        css={[
          PositionRelative,
          SizeStyle[size],
          size === 'small' && SmallSizeLayout,
          imagePreviewWrapStyle
        ]}
        key={uploadFile.id ?? uploadFile.url}
      >
        <UploadImagePreviewMarkup
          uploadFile={uploadFile}
          onDeletePhoto={onDeletePhoto}
          isMultiple={isMultiple}
          ratio={ratio}
          size={size}
        />
      </div>
    )
  );

  const isEmptyState = !isUploaded && (uploadFiles === undefined || uploadFiles.length === 0);

  const uploaderLayout = isMultiple ? (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId={id ? `multiple-uploader-${id}` : 'multiple-uploader'}
        direction="horizontal"
      >
        {(provided) => (
          <ul
            ref={provided.innerRef}
            {...provided.droppableProps}
            css={MultipleUploaderContainerStyle}
            id={id}
          >
            {(isLoading && <UploadLoadingSpinner isMultiple />) ||
              ((limit === undefined || uploadFiles === undefined || limit > uploadFiles.length) && (
                <DropZone
                  cssStyle={MultipleDropZoneHeightStyle}
                  ratio={ratio}
                  size={size}
                  onChangeFile={onChangeFile}
                  multiple
                  notAcceptFiles={setRejectedFiles}
                  accepts={accepts}
                />
              ))}

            {imagePreviewMarkups}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  ) : (
    <>
      {!isUploaded && isLoading ? (
        <UploadLoadingSpinner ratio={ratio} size={size} isMultiple={false} />
      ) : (
        <div css={[BaseSingleUploaderContainer, SingleContainerStateStyle]} id={id}>
          {isEmptyState && (
            <DropZone
              ratio={ratio}
              size={size}
              onChangeFile={onChangeFile}
              notAcceptFiles={setRejectedFiles}
              accepts={accepts}
            />
          )}
          {(isUploaded || (uploadFiles && uploadFiles.length > 0)) && imagePreviewMarkups}

          {isEmptyState && !isLoading && (
            <div css={[ColumnFlex, Width100, searchPhotoInputPosStyle]}>
              {hasSearch && searchInputMarkup}
              {unsplashSearchResult.length > 0 && photoListMarkup}
            </div>
          )}
        </div>
      )}
    </>
  );

  // AI PROJECT AREA ------------------------------------------------------------
  const aiImgDataRef = useRef<string | null>(null);
  const aiDescDataRef = useRef<string | null>(null);
  const aiImgTitleRef = useRef<string | null>(null);

  const resetAiData = () => {
    aiImgDataRef.current = null;
    aiDescDataRef.current = null;
    aiImgTitleRef.current = null;
  };

  const {
    trigger: generateAiContent,
    isLoading: isGeneratingAiContent,
    error: generateAiContentError
  } = usePostRequest<CommonResponseModel<GenerateResourceResponseModel>>(
    `/apps/${appId}/ai/generations`,
    { skipGlobalErrorToast: true } // 전역 토스트 비활성화
  );
  const { download: downloadFile } = useBrowserDownload();

  // AI 이미지 생성 액션 함수
  const handleAiImageGenerate = async () => {
    try {
      closeModal();
      track.onClick({
        clickEventName: ClickEventName.click_editgame_modal_aigenerate_image,
        params: {
          channel_id: appId,
          project_name: sessionStorage.getItem('projectName'),
          project_id: projectId,
          image_style: aiImgTitleRef.current,
          prompt: aiDescDataRef.current
        }
      });
      const result = await generateAiContent({
        generateType: 'Image',
        input: {
          prompt: aiDescDataRef.current
            ? `${aiImgDataRef.current},${aiDescDataRef.current}`
            : `${aiImgDataRef.current}`,
          aspectRatio: aiImageRatio
        },
        projectId
      });

      if (result?.data) {
        try {
          setUploadFiles((uploadFiles) => [
            ...(uploadFiles ?? []),
            { id: result.data.fileId, url: result.data.url }
          ]);
          await downloadFile(result.data.url, `ai-image-${result.data.fileId}`);
        } catch {
          setAlertContent({
            title: '다운로드 실패',
            message: '브라우저에서 파일 저장을 시작하지 못했어요. 다운로드 권한을 확인해 주세요.'
          });
        }
        resetAiData();
        creditRefetch();
        closeModal();
      } else {
        setAlertContent({
          title: 'AI 생성 실패',
          message: '이미지 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    } catch (err) {
      const errorTitle = (err as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;

      if (errorTitle === 'NotEnoughCredit') {
        toast({
          type: 'error',
          message: '크레딧이 모두 소진됐어요. 내일 다시 이용해 주세요.'
        });
      } else {
        toast({
          type: 'error',
          message: '이미지 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    }
  };

  const handleSingleAiImageGenerate = async () => {
    track.onClick({
      clickEventName: ClickEventName.click_editgame_modal_aigenerate_image,
      params: {
        channel_id: appId,
        project_name: sessionStorage.getItem('projectName'),
        project_id: projectId,
        image_style: aiImgTitleRef.current,
        prompt: aiDescDataRef.current
      }
    });
    try {
      closeModal();
      const result = await generateAiContent({
        generateType: 'Image',
        input: {
          prompt: aiDescDataRef.current
            ? `${aiImgDataRef.current},${aiDescDataRef.current}`
            : `${aiImgDataRef.current}`,
          aspectRatio: aiImageRatio
        },
        projectId
      });

      if (result?.data) {
        try {
          setUploadFiles(() => [...[{ id: result.data.fileId, url: result.data.url }]]);
          await downloadFile(result.data.url, `ai-image-${result.data.fileId}`);
        } catch {
          setAlertContent({
            title: '다운로드 실패',
            message: '브라우저에서 파일 저장을 시작하지 못했어요. 다운로드 권한을 확인해 주세요.'
          });
        }
        resetAiData();
        creditRefetch();
        closeModal();
      } else {
        setAlertContent({
          title: 'AI 생성 실패',
          message: '이미지 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    } catch (err) {
      const errorTitle = (err as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;
      if (errorTitle === 'NotEnoughCredit') {
        toast({
          type: 'error',
          message: '크레딧이 모두 소진됐어요. 내일 다시 이용해 주세요.'
        });
      } else {
        toast({
          type: 'error',
          message: '이미지 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.'
        });
      }
    }
  };

  const openCreateAiImageModal = (isSingle = false) => {
    track.onPageViewMixpanel(PageViewEventName.view_editgame_modal_aigenerate_image, {
      channel_id: appId,
      project_name: sessionStorage.getItem('projectName'),
      project_id: projectId
    });
    openModal({
      title: 'AI 이미지 생성',
      size: isMobile ? 'small' : 'large',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <div css={ModalContentContainer}>
          {/* Content 영역 */}
          <div css={ModalContentArea}>
            <CreateAiImageModalTemplate
              onStyleDataChange={(styleData) => {
                aiImgDataRef.current = styleData?.imageStylePrompt ?? '';
                aiImgTitleRef.current = styleData?.titleEn ?? '';
              }}
              onDescriptionChange={(description) => {
                aiDescDataRef.current = description;
              }}
            />
          </div>

          {/* Divider */}
          <div css={ModalDivider} />

          {/* Custom Footer */}
          <div css={ModalFooterContainer}>
            <AiUsageNotice />

            {/* 오른쪽 버튼 영역 */}
            <AiCreateButton
              creditValue={
                aiProductData?.data.find((product) => product.generateType === 'Image')
                  ?.creditPrice ?? 10
              }
              width="146px"
              height="40px"
              onClick={isSingle ? handleSingleAiImageGenerate : handleAiImageGenerate}
            />
          </div>
        </div>
      ),
      onClose: () => {
        closeModal();
        resetAiData();
      }
    });
  };

  // Multiple 레이아웃용 드롭존/로딩 컴포넌트 렌더링
  const renderMultipleDropZoneArea = () => {
    // 1순위: 업로드 제한 확인
    const canUploadMore =
      limit === undefined || uploadFiles === undefined || limit > uploadFiles.length;
    if (!canUploadMore) {
      return null;
    }

    // 2순위: 일반 파일 업로드 로딩 중
    if (isLoading || buttonFileUpload.isLoading) {
      return (
        <div css={[MultipleDropZoneHeightStyle, CenterLayout]}>
          <UploadLoadingSpinner isMultiple />
        </div>
      );
    }

    // 3순위: AI 생성 중
    if (isGeneratingAiContent) {
      return (
        <div css={MultipleDropZoneHeightStyle}>
          <AiLoadingBox size={size} ratio={ratio} />
        </div>
      );
    }

    // 4순위: 일반 드롭존
    return (
      <DropZoneV2
        cssStyle={MultipleDropZoneHeightStyle}
        ratio={ratio}
        size={size}
        onChangeFile={onChangeFile}
        multiple
        onRejectFiles={setRejectedFiles}
        accepts={accepts}
      />
    );
  };

  // Single 레이아웃용 메인 컨텐츠 렌더링
  const renderSingleMainContent = () => {
    // 1순위: AI 생성 중 (기존 로직과 동일)
    if (isGeneratingAiContent) {
      return (
        <div css={MultipleDropZoneHeightStyle}>
          <AiLoadingBox size={size} ratio={ratio} />
        </div>
      );
    }

    // 2순위: 빈 상태 - 드롭존 표시 (기존 조건 유지)
    if (isEmptyState && !isGeneratingAiContent) {
      return (
        <DropZoneV2
          ratio={ratio}
          size={size}
          onChangeFile={onChangeFile}
          onRejectFiles={setRejectedFiles}
          accepts={accepts}
        />
      );
    }

    // 3순위: 업로드된 이미지들 표시 (기존 조건 유지)
    if ((isUploaded || (uploadFiles && uploadFiles.length > 0)) && !isGeneratingAiContent) {
      return imagePreviewMarkups;
    }

    return null;
  };

  const convertUseForToMixpanelEventName = (useFor: string) => {
    switch (useFor) {
      case 'project-horizontal':
        return ClickEventName.click_editgame_theme_mainimage_horizontal_aigenerate;
      case 'project-vertical':
        return ClickEventName.click_editgame_theme_mainimage_vertical_aigenerate;
      case 'project-background':
        return ClickEventName.click_editgame_theme_backgroundimage_aigenerate;
      case 'project-mission-background':
        return ClickEventName.click_editgame_theme_missionbackgroundimage_aigenerate;
      case 'introduction-image':
        return ClickEventName.click_editgame_gamesetting_gameintroduction_image_aigenerate;
      case 'mission-card-image':
        return ClickEventName.click_editgame_mission_missioncardimage_aigenerate;
      case 'item-image':
        return ClickEventName.click_editgame_item_itemmodal_image_aigenerate;
      case 'item-icon':
        return ClickEventName.click_editgame_item_itemmodal_icon_aigenerate;
      case 'edit-item-image':
        return ClickEventName.click_editgame_item_itemmodal_image_aigenerate;
      case 'edit-item-icon':
        return ClickEventName.click_editgame_item_itemmodal_icon_aigenerate;
      default:
        return ClickEventName.click_editgame_theme_mainimage_horizontal_aigenerate;
    }
  };

  // Single 레이아웃용 하단 영역 렌더링
  const renderSingleBottomSection = () => {
    return (
      <div css={[ColumnFlex, Width100, searchPhotoInputPosStyle]}>
        <div css={UploadBottomSection}>
          {!isGeneratingAiContent && (
            <div css={UploadBottomSection}>
              <Typography type="body" textColor="initial">
                JPG, PNG &#40;4MB 이하&#41;
              </Typography>
              <div css={AiCreateButtonContainerStyle}>
                <Button
                  type="basic"
                  size="small"
                  icon="upload_solid"
                  disabled={buttonFileUpload.isLoading}
                  cssStyle={css`
                    border-radius: ${spacing.common.small};
                  `}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = isMultiple;
                    input.onchange = (e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.files && target.files.length > 0) {
                        startLoading(); // 업로드 시작 시 isLoading을 true로 설정
                        failUploaded(); // 업로드 상태 초기화
                        buttonFileUpload.uploadFiles(Array.from(target.files));
                      }
                    };
                    input.click();
                  }}
                >
                  파일 선택
                </Button>
                <AiCreateButton
                  creditValue={
                    aiProductData?.data.find((product) => product.generateType === 'Image')
                      ?.creditPrice ?? 10
                  }
                  onClick={() => {
                    checkCreditAndExecute(() => {
                      openCreateAiImageModal(true);
                      if (imageUseForType === 'mission-card-image') {
                        const missionSessionData = JSON.parse(
                          sessionStorage.getItem('missionSessionData') ?? '{}'
                        );
                        // mixpanel event data test
                        console.log('mixpanel event data', {
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId,
                            mission_id: missionSessionData.id,
                            mission_name: missionSessionData.name
                          }
                        });
                        track.onClick({
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId,
                            mission_id: missionSessionData.id,
                            mission_name: missionSessionData.name
                          }
                        });
                      }
                      if (
                        imageUseForType !== 'mission-card-image' &&
                        imageUseForType !== 'item-image' &&
                        imageUseForType !== 'item-icon' &&
                        imageUseForType !== 'edit-item-image' &&
                        imageUseForType !== 'edit-item-icon'
                      ) {
                        // mixpanel event data test
                        console.log('mixpanel event data', {
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId
                          }
                        });
                        track.onClick({
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId
                          }
                        });
                      }
                      if (imageUseForType === 'item-image' || imageUseForType === 'item-icon') {
                        // mixpanel event data test
                        track.onClick({
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId
                          }
                        });
                      }
                      if (
                        imageUseForType === 'edit-item-image' ||
                        imageUseForType === 'edit-item-icon'
                      ) {
                        // mixpanel event data test
                        console.log('mixpanel event data', {
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId,
                            modal_type: 'edit'
                          }
                        });
                        track.onClick({
                          clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                          params: {
                            channel_id: appId,
                            project_name: sessionStorage.getItem('projectName'),
                            project_id: projectId
                          }
                        });
                      }
                    }, aiProductData?.data.find((product) => product.generateType === 'Image')?.creditPrice ?? 10);
                  }}
                />
              </div>
              {/* <Typography type="caption" textColor="subdued">
                *AI가 &#39;미션&#39;의 내용을 참고하여 파일을 만들어요.
              </Typography> */}
            </div>
          )}
          {isGeneratingAiContent && (
            <div css={UploadBottomSection}>
              <Typography type="body" textColor="initial">
                JPG, PNG &#40;4MB 이하&#41;
              </Typography>

              <Typography type="caption" style={{ color: '#556CEF' }}>
                *생성 중 페이지를 벗어나면
                <Typography as="span" type="caption" style={{ color: '#556CEF', fontWeight: 700 }}>
                  {' '}
                  생성 작업이 초기화
                </Typography>
                되고,
                <br />
                <Typography as="span" type="caption" style={{ color: '#556CEF', fontWeight: 700 }}>
                  사용한 크레딧은 환불되지 않아요.
                </Typography>
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  };

  // AI 프로젝트용 업로더 레이아웃
  const uploaderAiProjectLayout = isMultiple ? (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId={id ? `multiple-uploader-${id}` : 'multiple-uploader'}
        direction="horizontal"
      >
        {(provided) => (
          <ul
            ref={provided.innerRef}
            {...provided.droppableProps}
            css={MultipleUploaderContainerStyle}
            id={id}
          >
            {renderMultipleDropZoneArea()}
            {imagePreviewMarkups}
          </ul>
        )}
      </Droppable>
      {!isGeneratingAiContent && (
        <div css={UploadBottomSection}>
          <Typography type="body" textColor="initial">
            JPG, PNG &#40;4MB 이하&#41;
          </Typography>
          <div css={AiCreateButtonContainerStyle}>
            <Button
              type="basic"
              size="small"
              icon="upload_solid"
              disabled={buttonFileUpload.isLoading}
              cssStyle={css`
                border-radius: ${spacing.common.small};
              `}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.multiple = isMultiple;
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  if (target.files && target.files.length > 0) {
                    startLoading(); // 업로드 시작 시 isLoading을 true로 설정
                    failUploaded(); // 업로드 상태 초기화
                    buttonFileUpload.uploadFiles(Array.from(target.files));
                  }
                };
                input.click();
              }}
            >
              파일 선택
            </Button>
            <AiCreateButton
              creditValue={
                aiProductData?.data.find((product) => product.generateType === 'Image')
                  ?.creditPrice ?? 10
              }
              onClick={() => {
                checkCreditAndExecute(() => {
                  openCreateAiImageModal(false);

                  // mixpanel event data test
                  // console.log('mixpanel event data', {
                  //   clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                  //   params: {
                  //     channel_id: appId,
                  //     project_name: sessionStorage.getItem('projectName'),
                  //     project_id: projectId
                  //   }
                  // });
                  track.onClick({
                    clickEventName: convertUseForToMixpanelEventName(imageUseForType ?? ''),
                    params: {
                      channel_id: appId,
                      project_name: sessionStorage.getItem('projectName'),
                      project_id: projectId
                    }
                  });
                }, aiProductData?.data.find((product) => product.generateType === 'Image')?.creditPrice ?? 10);
              }}
            />
          </div>
          {/* <Typography type="caption" textColor="subdued">
            *AI가 &#39;미션&#39;의 내용을 참고하여 파일을 만들어요.
          </Typography> */}
        </div>
      )}
      {isGeneratingAiContent && (
        <div css={UploadBottomSection}>
          <Typography type="body" textColor="initial">
            JPG, PNG &#40;4MB 이하&#41;
          </Typography>

          <Typography type="caption" style={{ color: '#556CEF' }}>
            *생성 중 페이지를 벗어나면
            <Typography as="span" type="caption" style={{ color: '#556CEF', fontWeight: 700 }}>
              생성 작업이 초기화
            </Typography>
            되고,{' '}
            <Typography as="span" type="caption" style={{ color: '#556CEF', fontWeight: 700 }}>
              사용한 크레딧은 환불되지 않아요.
            </Typography>
          </Typography>
        </div>
      )}
    </DragDropContext>
  ) : (
    <>
      {!isUploaded && (isLoading || buttonFileUpload.isLoading) && !isGeneratingAiContent ? (
        <UploadLoadingSpinner ratio={ratio} size={size} isMultiple={false} />
      ) : (
        <div css={[BaseSingleUploaderContainer, SingleContainerStateStyle]} id={id}>
          {renderSingleMainContent()}
          {renderSingleBottomSection()}
        </div>
      )}
    </>
  );

  return (
    <>
      <section>
        <Modal {...modal} />
        <Modal {...aiCreditModal} />
        {isAiProject ? uploaderAiProjectLayout : uploaderLayout}
      </section>
      <Modal
        open={isAlertOpen}
        title={alertContent?.title}
        onClose={closeAlert}
        primaryAction={{ content: '확인', type: 'primary', onAction: closeAlert }}
        cssStyle={TextAlignCenter}
      >
        {alertContent?.message}
      </Modal>
    </>
  );
};

export default memo(Uploader);

interface UploadLoadingSpinnerProps extends Pick<IDropZoneProps, 'ratio' | 'size'> {
  isMultiple: boolean;
}

export function UploadLoadingSpinner({
  ratio = 'fullWidth',
  size = 'medium',
  isMultiple
}: UploadLoadingSpinnerProps) {
  const MultipleDropZoneLoadingSize = css`
    min-width: ${MULTIPLE.WIDTH[size]};
  `;

  return (
    <div
      css={[
        RatioStyle[ratio],
        SizeStyle[size],
        CenterLayout,
        isMultiple && MultipleDropZoneLoadingSize
      ]}
    >
      <Icon icon="spinner_solid" size="48px" spinning />
    </div>
  );
}

const imagePreviewWrapStyle = css`
  width: fit-content;
  margin-right: ${spacing.common.small};

  &:last-of-type {
    margin-right: 0px;
  }
`;

const CenterLayout = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullContents = css`
  width: 100%;
  height: 100%;
`;

const GrowStyle = css`
  flex-grow: 1;

  ${onMobile} {
    flex-grow: initial;
  }
`;

const BaseSingleUploaderContainer = css`
  display: flex;
  flex-direction: row;

  ${onMobile} {
    flex-direction: column;
  }
`;

const MultipleUploaderContainerStyle = css`
  display: flex;
  flex-wrap: nowrap;
  padding: ${spacing.common.small};
  overflow-x: auto;
  background-color: ${color.surface_default_subdued};
  border-radius: ${spacing.common.xsmall};
`;

const BaseImageStyle = css`
  display: block;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: ${spacing.common.small};
  cursor: pointer;
  ${shadow.toast};
`;

const DeleteButtonStyle = css`
  position: absolute;
  top: 0;
  height: 42px;
  width: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
  display: flex;
  align-items: baseline;
  justify-content: end;

  border-top-right-radius: ${spacing.common.small};
  border-top-left-radius: ${spacing.common.small};
`;

const PosIconButtonStyle = css`
  margin-right: ${spacing.common.xsmall};
  margin-top: ${spacing.common.xsmall};
`;

const PhotoListContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${spacing.common.small};
  padding: ${spacing.common.small};
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.common.xsmall};

  height: 408px;
  overflow-y: auto;
`;

const PhotoCardStyle = css`
  width: 33.3333%;
  height: 33.3333%;
  padding: ${spacing.common.small};
  cursor: pointer;
`;

const PhotoImgStyle = css`
  background-size: cover;
  background-position: center;
`;

const searchPhotoInputPosStyle = css`
  ${onMobile} {
    margin-top: ${spacing.common.medium};
  }
`;

const SmallSizeLayout = css`
  height: 100%;
`;

const imagePreviewDialog = css`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;

  /* 디자인시스템 모달 백드롭 스타일에서 스타일 시트를 가져왔습니다. */
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  animation: fadeIn 200ms 1 forwards;
  opacity: 1;
  will-change: opacity;
  cursor: url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.9095 12.0003L19.6786 6.2311L20.8684 5.04138C21.0439 4.86587 21.0439 4.58067 20.8684 4.40517L19.5954 3.13219C19.4199 2.95669 19.1347 2.95669 18.9592 3.13219L12.0003 10.0911L5.04138 3.13163C4.86587 2.95612 4.58067 2.95612 4.40517 3.13163L3.13163 4.40461C2.95612 4.58011 2.95612 4.86531 3.13163 5.04081L10.0911 12.0003L3.13163 18.9592C2.95612 19.1347 2.95612 19.4199 3.13163 19.5954L4.40461 20.8684C4.58011 21.0439 4.86531 21.0439 5.04081 20.8684L12.0003 13.9095L17.7695 19.6786L18.9592 20.8684C19.1347 21.0439 19.4199 21.0439 19.5954 20.8684L20.8684 19.5954C21.0439 19.4199 21.0439 19.1347 20.8684 18.9592L13.9095 12.0003Z' fill='white' /%3E%3C/svg%3E"),
    auto;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  > div {
    position: relative;
    cursor: default;
    user-select: none;

    img {
      max-width: 80vh;
      max-height: 90vh;
      border-radius: ${spacing.common.small};
    }
  }
`;

const openImageErrorAlertStyle = css`
  font-size: 14;
  font-weight: 400;
  line-height: 20px;
  text-align: left;

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const AiCreateButtonContainerStyle = css`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: start;
  gap: ${spacing.common.small};
`;

// AI Image Modal Styles
const ModalContentContainer = css`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalContentArea = css`
  flex: 1;
  overflow: auto;
`;

const ModalDivider = css`
  width: calc(100% + 48px);
  height: 1px;
  margin-top: 24px;
  margin-left: -24px;
  background-color: #e4e5e7;
`;

const ModalFooterContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: -24px;
  padding: 16px 0px;
  background-color: #ffffff;
  gap: 16px;
  flex-wrap: wrap;
`;

const UploadBottomSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.small};
  margin-top: ${spacing.common.small};
`;
